import { useEffect, useRef, useState } from 'react';

export interface ResourceTickerOptions {
  /** Production per hour. The client interpolates between server snapshots. */
  ratePerHour?: number;
  /** Storage ceiling — the ticker stops climbing once full. */
  capacity?: number;
  /** Redraw frequency in ms. 100 ms reads as continuous without burning frames. */
  intervalMs?: number;
}

/**
 * Client-side resource interpolation.
 *
 * The GDD is explicit about this: counters must climb continuously "ohne den
 * Server mit Dauer-Anfragen zu belasten". So the server sends an authoritative
 * amount plus a rate, and this hook fills in the seconds between polls. Whenever
 * a fresh `amount` arrives it re-anchors — the server always wins, and drift
 * can never accumulate.
 *
 * @param amount Authoritative amount from the last server snapshot.
 */
export function useResourceTicker(
  amount: number,
  { ratePerHour = 0, capacity, intervalMs = 100 }: ResourceTickerOptions = {},
): number {
  const [display, setDisplay] = useState(amount);
  const anchorRef = useRef({ amount, at: Date.now() });

  // Re-anchor on every authoritative update.
  useEffect(() => {
    anchorRef.current = { amount, at: Date.now() };
    setDisplay(amount);
  }, [amount]);

  useEffect(() => {
    if (!ratePerHour) return;

    const perMs = ratePerHour / 3_600_000;
    const id = window.setInterval(() => {
      const { amount: base, at } = anchorRef.current;
      const projected = base + (Date.now() - at) * perMs;
      setDisplay(capacity === undefined ? projected : Math.min(projected, capacity));
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [ratePerHour, capacity, intervalMs]);

  return display;
}
